import type { FC } from 'react';

import { Flex, Avatar, Tooltip, Typography, type FlexProps } from 'antd';
import styles from './space.module.scss';
import classNames from 'classnames';
import { ConditionalWrapper } from '@shared/ui/conditional-wrapper';
import { useTranslation } from 'react-i18next';

export interface SpaceProps extends Omit<FlexProps, 'children'> {
  size?: 'sm' | 'md';
  title?: string;
  caption?: string;
  maxTitleLength?: number;
  isTooltipVisible?: boolean;
}

const MAX_CAPTION_LENGTH = 35;
const MAX_TITLE_LENGTH = 25;

export const Space: FC<SpaceProps> = ({
  size = 'sm',
  title,
  caption,
  maxTitleLength = MAX_TITLE_LENGTH,
  isTooltipVisible = true,
  ...flexProps
}) => {
  const { t } = useTranslation('translation');
  const { Text } = Typography;

  const hasLongTitle = title && title?.length > maxTitleLength;
  const titleSlice = hasLongTitle ? `${title.slice(0, maxTitleLength)}...` : title;

  const hasLongCaption = caption && caption?.length > MAX_CAPTION_LENGTH;
  const captionSlice = hasLongCaption ? `${caption.slice(0, MAX_CAPTION_LENGTH)}...` : caption;

  return (
    <Flex {...flexProps} align="center" className={styles.root} gap="8px">
      <Flex className={styles.avatar_container} align="center" justify="center">
        {
          <Avatar shape="square" className={classNames(styles.avatar_container, styles.avatar)}>
            {(title?.slice(0, 2) || t('common.initialsPlaceholder'))?.toLocaleUpperCase()}
          </Avatar>
        }
      </Flex>
      <Flex className={styles.container} vertical gap="2px">
        {title && (
          <Text className={styles.title}>
            <ConditionalWrapper
              condition={isTooltipVisible && (hasLongTitle || false)}
              wrapper={(children) => (
                <Tooltip title={title} placement="top" className={styles.tooltip}>
                  {children}
                </Tooltip>
              )}
            >
              <span>{titleSlice}</span>
            </ConditionalWrapper>
          </Text>
        )}

        {caption && (
          <Text className={styles.caption}>
            <ConditionalWrapper
              condition={isTooltipVisible && (hasLongCaption || false)}
              wrapper={(children) => (
                <Tooltip title={caption} placement="top" className={styles.tooltip}>
                  {children}
                </Tooltip>
              )}
            >
              <span>{captionSlice}</span>
            </ConditionalWrapper>
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
